// THE OPERATIONS INBOX PAGE'S WORDS, NUMBERS AND ORDER: pure, so every sentence
// on /inbox is a test away from being checked, and the page and its board only
// lay out what this returns.
//
// Two halves:
//
//   THE CARD, as the page draws it (`InboxCard`): lib/server/ops-inbox-page.ts
//   reads the inbox's `ops_card` rows and turns each into one of these with
//   `inboxCard`, so the counter, the late tag, the "+N new" and who finished it
//   are decided here, once, from the row the tick wrote.
//
//   THE PAGE (`inboxPage`): the scope, the pod and brand tiers, the tabs and the
//   workstream row, all from the cards in scope and the URL; and the board's
//   order (`ageSections`, `cardGroups`), which the client board calls because
//   the grouping and density are the viewer's saved choice.
//
// ⚠️ THE WHOLE READING IS THE URL (`?scope=`, `?pod=`, `?brand=`, `?tab=`,
// `?ws=`), so a pasted link opens what the sender saw; the view choices (group
// by Age or Task, All / To do / Done, rows or cards, which age sections are
// folded) are the viewer's own and live in their browser.
//
// ⚠️ AN ESCALATED-AWAY CARD COUNTS IN NONE OF ITS OLD SEAT'S NUMBERS: it is shown
// apart, at the end of its group before the done cards, and every count below
// leaves it out.

import { kindDef, OPS_KINDS, WORKSTREAMS, isWorkstream, type Cadence, type Workstream } from '@/lib/ops-inbox-kinds';
import { OPERATIONS_TIME_ZONE, operationsDay } from '@/lib/throughput-operations';
import { addDaysUtc, daysBetweenUtc, dowOf, formatShort, formatWithWeekday } from '@/lib/schedule-dates';
import { AFFILIATES_PATH, brandPageHref } from '@/lib/affiliate-pages';
import { lensHref } from '@/lib/lens-url';
import { canSnooze, endedOf, type CardDetail, type DoneVia, type EndedWhy } from '@/lib/ops-inbox-cards';
import { listedScripts } from '@/lib/ops-inbox-scripts';
import { gradeTone, type GradeTone } from '@/lib/grade-tone';
import { reportStateOf } from '@/lib/ops-inbox-reports';
import { closeHasTime, messagesClosedOf, messagesClosedWords } from '@/lib/ops-inbox-comms';
import { cardItemOf, completeClosedOf, completeClosedWords, reviewClosedWords, reviewGoneOf } from '@/lib/ops-inbox-items';
import { ACTION_ITEMS_PATH } from '@/lib/client-slack-action-items';
import { INBOX_PATH as CLIENT_INBOX_PATH } from '@/lib/client-slack-inbox';
import { reportState, type ReportStateInput } from '@/lib/weekly-report';
import { query } from '@/lib/url-query';

export const INBOX_PATH = '/inbox';

// ─── The reading ────────────────────────────────────────────────────────────

/** My: the seats I hold or cover. Active: every card in an active pod × workstream cell. All: every card. */
export type InboxScope = 'my' | 'active' | 'all';
export const SCOPES: readonly { key: InboxScope; label: string; title: string }[] = [
  { key: 'my', label: 'My', title: 'Cards in seats you hold or cover' },
  { key: 'active', label: 'Active', title: 'Every card in an active pod and workstream' },
  { key: 'all', label: 'All', title: 'Every card, active or not' },
];
export const isScope = (v: unknown): v is InboxScope => v === 'my' || v === 'active' || v === 'all';

export type InboxTab = 'today' | 'upcoming' | 'snoozed' | 'archived';
export const TABS: readonly { key: InboxTab; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'snoozed', label: 'Snoozed' },
  { key: 'archived', label: 'Archived' },
];

/** The workstream row's value for a card with no workstream (an ad-hoc task with none picked). */
export const AD_HOC = 'ad-hoc';
export type WsPick = Workstream | typeof AD_HOC;

/** The `?pod=` of the No pod seat's cards, the scope bar's own spelling (lib/scope-tiers.ts `NO_POD`). */
export const NO_POD_PICK = 'none';

export interface InboxReading {
  /** Null: the scope the viewer's inbox opens on, left out of the URL. */
  scope: InboxScope | null;
  /** A pod's lead slug, or `none` for the No pod cards. */
  pod: string | null;
  brand: string | null;
  tab: InboxTab;
  ws: WsPick | null;
}

const one = (v: string | string[] | undefined): string | undefined => (Array.isArray(v) ? v[0] : v);

/** The URL read back. A value that names nothing is no pick, never a throw. */
export function parseReading(sp: Record<string, string | string[] | undefined>): InboxReading {
  const scope = one(sp.scope);
  const tab = one(sp.tab);
  const ws = one(sp.ws);
  const pod = one(sp.pod)?.trim();
  const brand = one(sp.brand)?.trim();
  return {
    scope: isScope(scope) ? scope : null,
    pod: pod ? pod : null,
    brand: brand ? brand : null,
    tab: TABS.some((t) => t.key === tab) ? (tab as InboxTab) : 'today',
    ws: ws === AD_HOC || isWorkstream(ws) ? ws : null,
  };
}

/** The reading as a query, in one order, leaving out what is the default. */
export function readingQuery(r: InboxReading): [string, string][] {
  const out: [string, string][] = [];
  if (r.scope) out.push(['scope', r.scope]);
  if (r.pod) out.push(['pod', r.pod]);
  if (r.brand) out.push(['brand', r.brand]);
  if (r.tab !== 'today') out.push(['tab', r.tab]);
  if (r.ws) out.push(['ws', r.ws]);
  return out;
}

export const inboxHref = (r: InboxReading, as: string | null): string => lensHref(`${INBOX_PATH}${query(readingQuery(r))}`, as);

// ─── The card ───────────────────────────────────────────────────────────────

/** The script editor, which opens a script's whole week. */
export const SCRIPT_EDITOR_PATH = '/scripts/editor';

/** A brand's weekly reports, each week's report under it. */
const reportsPath = (brand: string): string => `/brands/${brand}/weekly`;

/** A client's First 30 checklist. */
const checklistPath = (brand: string): string => `/onboarding/first30/c/${brand}`;

/** The card's own string from its `detail_json`, or null. */
const detailText = (d: CardDetail | null, k: string): string | null => {
  const v = d?.[k];
  return typeof v === 'string' && v !== '' ? v : null;
};

/**
 * WHERE A CARD'S WORK IS: its page, with `?card=<id>` so the page can say
 * which card it was opened from, and the lens kept. Null for a kind with no
 * page (yet): the card is drawn, not linked. A review-scripts card opens its
 * week in the editor, by the first script it lists (`detail`).
 */
export function cardHref(kind: string, brand: string | null, cardId: string, as: string | null, detail: CardDetail | null = null): string | null {
  if (brand === null) return null;
  switch (kind) {
    case 'review-scripts': {
      const first = listedScripts(detail)[0];
      return first ? lensHref(`${SCRIPT_EDITOR_PATH}${query([['id', first.id], ['card', cardId]])}`, as) : null;
    }
    case 'review-sample-requests':
      return brandPageHref(brand, 'requests', as, { card: cardId });
    case 'review-messages':
      return brandPageHref(brand, 'messages', as, { card: cardId });
    case 'launch-campaign':
      return lensHref(`${AFFILIATES_PATH}/${brand}/new${query([['card', cardId]])}`, as);
    case 'weekly-report': {
      const week = detailText(detail, 'week');
      return week ? lensHref(`${reportsPath(brand)}/${week}${query([['card', cardId]])}`, as) : null;
    }
    case 'onboarding-section': {
      // The checklist opens with the card's section expanded and scrolled to.
      const section = detailText(detail, 'section');
      return lensHref(`${checklistPath(brand)}${query([...(section ? [['section', section] as [string, string]] : []), ['card', cardId]])}`, as);
    }
    case 'client-messages':
      // The Slack messages only: the card counts no meeting follow-up, so the list it opens shows none.
      return lensHref(`${CLIENT_INBOX_PATH}${query([['source', 'slack'], ['brand', brand], ['card', cardId]])}`, as);
    case 'review-action-items':
      // The brand's items waiting to be confirmed: the page's "to confirm" over All, the card's own units.
      return lensHref(`${ACTION_ITEMS_PATH}${query([['brand', brand], ['tab', 'all'], ['only', 'confirm'], ['card', cardId]])}`, as);
    case 'complete-action-item': {
      // The item's own popup, over its brand's whole list (it may be the client's ball, off We owe).
      const item = detailText(detail, 'item');
      return item ? lensHref(`${ACTION_ITEMS_PATH}/${item}${query([['brand', brand], ['tab', 'all'], ['card', cardId]])}`, as) : null;
    }
    default:
      return null;
  }
}

/**
 * WHERE A CARD'S WORK IS, AS A PLACE: the task chip carries the card while
 * the reader stays under this path (the brand's tabs, a conversation, the New
 * campaign steps), and lets it go when they leave. Null for a kind with no page.
 */
export function cardPlace(kind: string, brand: string | null): string | null {
  if (brand === null) return null;
  switch (kind) {
    case 'review-sample-requests':
    case 'review-messages':
    case 'launch-campaign':
      return `${AFFILIATES_PATH}/${brand}`;
    case 'review-scripts':
      return SCRIPT_EDITOR_PATH;
    case 'weekly-report':
      return reportsPath(brand);
    case 'onboarding-section':
      return checklistPath(brand);
    case 'client-messages':
      return CLIENT_INBOX_PATH;
    case 'review-action-items':
    case 'complete-action-item':
      return ACTION_ITEMS_PATH;
    default:
      return null;
  }
}

/** Is `pathname` the place, or under it? */
export const underPlace = (pathname: string, place: string): boolean => pathname === place || pathname.startsWith(`${place}/`);

/** The age sections of Today, oldest first: the over-a-week one, a day each, then today's work. */
export const AGE_SECTIONS: readonly { key: string; label: string }[] = [
  { key: '7+', label: 'Late over a week' },
  ...[6, 5, 4, 3, 2, 1].map((n) => ({ key: String(n), label: `Late ${n} day${n === 1 ? '' : 's'}` })),
  { key: '0', label: 'Due today' },
];

/** Which age section a card sits in, by how many days late it is. */
export function ageSectionKey(lateDays: number): string {
  if (lateDays >= 7) return '7+';
  if (lateDays <= 0) return '0';
  return String(lateDays);
}

/** The tag that says when a card is due, in the badge's tones. */
export interface DueTag {
  tone: 'plain' | 'amber' | 'red' | 'green';
  text: string;
}

/** A complete card's action item, as a line: its title, its own due date, its assignee and whose ball it is. */
export interface ItemLine {
  id: string;
  title: string;
  /** "due Nov 5", or "no due date". */
  due: string;
  /** The assignee's first name, or null for none. */
  assignee: string | null;
  /** "We owe" / "Client owes", or null. */
  owes: string | null;
}

/** One script's pill on a review card: its title and grade in the grader's colour; struck once reviewed. */
export interface ScriptPill {
  title: string;
  grade: string | null;
  tone: GradeTone;
  done: boolean;
}

export interface InboxCard {
  id: string;
  kind: string;
  /** The task type's name; an overdue card's says which work is late ("Overdue footage"). */
  kindLabel: string;
  /** Null: an ad-hoc card with no workstream. */
  workstream: Workstream | null;
  cadence: string;
  brand: string | null;
  brandName: string;
  /** Who or what the card is about, as its first line: the creator for a script card, else the brand. */
  name: string;
  /** Its second line: the brand under a creator, else the pod (null: none). */
  sub: string | null;
  /** About something under a brand (a creator, an onboarding section): `sub` is the brand, and a No pod pill sits beside it rather than in its place. */
  brandSub: boolean;
  /** The pod's lead slug, `none` for a card on the No pod seat, null for a card with no brand. */
  pod: string | null;
  podLabel: string | null;
  dueOn: string;
  /** Whole days past due; 0 for a card due today, ahead, or of a kind that is never late. */
  lateDays: number;
  tab: InboxTab;
  done: boolean;
  /** A counting card's counter, the done count capped at the target. */
  counter: { done: number; target: number; newN: number } | null;
  /** The words after a counter: "replies owed". */
  unit: string | null;
  /** The sample requests' triage of who is waiting (yes / look / no). */
  triage: { yes: number; look: number; no: number } | null;
  /** A review card's scripts. */
  scripts: ScriptPill[] | null;
  /** A weekly report card's state, as the weekly list's chip paints it (`reportState`). */
  report: ReportStateInput | null;
  /** A complete card's action item; the rows view draws it as a line of its own. */
  item: ItemLine | null;
  /** A review card's items still to confirm, which it counts down; null on every other kind. */
  toReview: number | null;
  /** A line in place of a counter: a launch card's week, an overdue card's late work. */
  line: string | null;
  due: DueTag;
  /** Why a done card is done: "Done by Riley", "Done by Reacher", "Marked Final by Dana on Nov 5". */
  finished: string | null;
  seat: { label: string; empty: boolean } | null;
  /** On the No pod seat: shown only under All, with its pill. */
  noPod: boolean;
  /** Escalated away from the viewer's seats: "Escalated to Lead · Dana's pod". It counts in none of their numbers. */
  away: string | null;
  /** Escalated here (or seen from All): "From Ops · Dana's pod", and the escalation's line ("Escalated by Riley to …: <note>"). */
  from: { label: string; line: string | null } | null;
  /** In an active pod × workstream cell. */
  active: boolean;
  /** The page where the work is, with the card named; null for a kind with no page. */
  href: string | null;
  /** Opens the reminder slide-out in place, rather than a page: an overdue card, whose work is the creator's. */
  slideOut: boolean;
  /** An ad-hoc task's own words, and who added it ("you" for the viewer); it opens in a popup. Null on every other kind. */
  task: { title: string; body: string | null; addedBy: string | null } | null;
  /** The day a snoozed card comes back. */
  snoozedUntil: string | null;
  /** When and by whom it was archived ("Nov 3", "Riley"): the Archived tab's line. */
  archived: { on: string; by: string | null; at: number; what: string } | null;
  /** What the card's ⋯ menu offers (and an ad-hoc task's popup). */
  actions: CardActions;
  /** The rows view's one-line pill: who it is for and its number. */
  mini: { label: string; n: string };
}

/** What a person may do to a card from the inbox. `done` / `reopen`: an ad-hoc task's Mark done and Not done. */
export interface CardActions {
  snooze: boolean;
  /** Move a snoozed card's day: the snooze's own history says it moved. */
  resnooze: boolean;
  unsnooze: boolean;
  archive: boolean;
  unarchive: boolean;
  done: boolean;
  reopen: boolean;
  /** Hand it to another seat: a card to do, in an active cell (one out of view is no one's to pass on). */
  escalate: boolean;
}

const CADENCE_LABEL: Record<Cadence, string> = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', event: 'Event', 'ad-hoc': 'Ad hoc' };
const MONTH = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** 1479 → "1,479": prod's queues open in the thousands. */
export const num = (n: number): string => n.toLocaleString('en-US');

const plural = (n: number, word: string): string => `${num(n)} ${word}${n === 1 ? '' : 's'}`;

const clockFormatter = new Intl.DateTimeFormat('en-US', { timeZone: OPERATIONS_TIME_ZONE, hour: 'numeric', minute: '2-digit' });
/** An instant as the reporting timezone's clock: "3:56pm". */
export const clock = (at: number): string => clockFormatter.format(new Date(at)).replace(/\s?([AP])M$/, (_m, ap: string) => `${ap.toLowerCase()}m`);

/** What the server knows about a card beyond its row, to name it. */
export interface CardNames {
  brandName: string;
  /** The creator's name, for a card about a creator (the script kinds); null for one about a brand. */
  creatorName: string | null;
  pod: string | null;
  podLabel: string | null;
  seat: { label: string; empty: boolean } | null;
  noPod: boolean;
  /** The first name of whoever finished it, when someone did. */
  doneByName: string | null;
  /** A person's first name for an email (an action item's assignee). Absent: the email. */
  firstNameOf?: (email: string) => string;
  /** An ad-hoc task's: who added it, "you" for the viewer. */
  addedByName: string | null;
  /** Who archived it, "you" for the viewer. */
  archivedByName: string | null;
  away: string | null;
  from: { label: string; line: string | null } | null;
  active: boolean;
  href: string | null;
}

/** The `ops_card` columns the page reads (lib/ops-inbox-cards.ts `CardRow`). */
export interface CardFacts {
  cardId: string;
  kind: string;
  workstream: string | null;
  brandSlug: string | null;
  dueOn: string;
  state: 'todo' | 'done' | 'snoozed' | 'archived';
  target: number | null;
  doneN: number | null;
  doneVia: DoneVia | null;
  doneAt: number | null;
  detail: CardDetail | null;
  snoozedUntil: string | null;
  archivedAt: number | null;
  /** An ad-hoc task's own words. */
  title?: string | null;
  body?: string | null;
}

const detailNum = (d: CardFacts['detail'], k: string): number => {
  const v = d?.[k];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
};

/** An overdue card's name, by which work is late: "Overdue scripts", "Overdue footage", or both. */
export function overdueLabel(lateScripts: number, lateVideos: number): string {
  if (lateScripts > 0 && lateVideos > 0) return 'Overdue scripts and footage';
  return lateVideos > 0 ? 'Overdue footage' : 'Overdue scripts';
}

/** What is late, over how many weeks: "2 weeks · 3 scripts and 1 video not in". Null: nothing is. */
export function lateWorkLine(lateScripts: number, lateVideos: number, weeks: number): string | null {
  const what = [lateScripts > 0 ? plural(lateScripts, 'script') : '', lateVideos > 0 ? plural(lateVideos, 'video') : ''].filter(Boolean).join(' and ');
  return what ? `${weeks > 1 ? `${num(weeks)} weeks · ` : ''}${what} not in` : null;
}

/**
 * WHY A DONE OVERDUE CARD IS DONE. Done for the day: a reminder went, and it
 * comes back tomorrow while the creator is still late ("Reminder sent Wed Nov
 * 4, 10:00am by Riley"). Closed for good: the last thing that happened to the
 * work, the creator submitting ("Ben submitted the scripts on Nov 5") or the
 * dates moved ("Due dates moved by Lee on Nov 5"), else that nothing is late.
 */
export function overdueFinished(c: Pick<CardFacts, 'detail' | 'doneAt'>, n: Pick<CardNames, 'creatorName' | 'doneByName'>): string {
  const who = (n.creatorName ?? '').trim().split(/\s+/)[0] || 'The creator';
  const on = typeof c.detail?.closedOn === 'string' ? ` on ${formatShort(c.detail.closedOn)}` : '';
  if (detailNum(c.detail, 'owed') > 0) {
    const when = c.doneAt === null ? '' : ` ${formatWithWeekday(operationsDay(c.doneAt))}, ${clock(c.doneAt)}`;
    return `Reminder sent${when}${n.doneByName ? ` by ${n.doneByName}` : ''}`;
  }
  switch (c.detail?.closed) {
    case 'submitted':
      return `${who} submitted the ${typeof c.detail.closedWhat === 'string' ? c.detail.closedWhat : 'work'}${on}`;
    case 'moved':
      return `Due dates moved${n.doneByName ? ` by ${n.doneByName}` : ''}${on}`;
    default:
      return `Nothing of ${who === 'The creator' ? "the creator" : who}'s is late any more`;
  }
}

const doneOn = (doneAt: number | null): string => (doneAt === null ? '' : ` on ${formatShort(operationsDay(doneAt))}`);

/** Why a done weekly report card is done: "Marked Final by Dana on Nov 5". */
export function reportFinished(c: Pick<CardFacts, 'doneAt'>, n: Pick<CardNames, 'doneByName'>): string {
  return `Marked Final${n.doneByName ? ` by ${n.doneByName}` : ''}${doneOn(c.doneAt)}`;
}

/**
 * WHY A DONE ONBOARDING SECTION CARD IS DONE: "Section finished: Dana ticked
 * the last item on Nov 5", or that the section left the checklist.
 */
export function sectionFinished(c: Pick<CardFacts, 'detail' | 'doneAt'>, n: Pick<CardNames, 'doneByName'>): string {
  if (c.detail?.closed === 'removed') return 'Section removed from the checklist';
  const on = typeof c.detail?.closedOn === 'string' ? ` on ${formatShort(c.detail.closedOn)}` : doneOn(c.doneAt);
  return n.doneByName ? `Section finished: ${n.doneByName} ticked the last item${on}` : `Section finished${on}`;
}

/**
 * WHY A DONE UNANSWERED-MESSAGES CARD IS DONE: how the last of its messages
 * stopped waiting, "Replied in Slack by Dana at 3:12pm"; a reaction or the
 * model's read has no time and no one ("Acknowledged with a reaction in Slack").
 */
export function messagesFinished(c: Pick<CardFacts, 'detail' | 'doneAt'>, n: Pick<CardNames, 'doneByName'>): string {
  const how = messagesClosedOf(c.detail?.closed);
  const who = n.doneByName ?? detailText(c.detail, 'closedName');
  return `${messagesClosedWords(how, who)}${closeHasTime(how) && c.doneAt !== null ? ` at ${clock(c.doneAt)}` : ''}`;
}

/** Why a done review card is done: "Nothing left to review: Dana confirmed the last item on Nov 5". */
export function reviewFinished(c: Pick<CardFacts, 'detail' | 'doneAt'>, n: Pick<CardNames, 'doneByName'>): string {
  const why = reviewGoneOf(c.detail?.closed);
  const on = typeof c.detail?.closedOn === 'string' ? ` on ${formatShort(c.detail.closedOn)}` : doneOn(c.doneAt);
  return `Nothing left to review: ${reviewClosedWords(why, n.doneByName)}${why === 'gone' ? '' : on}`;
}

/** Why a done complete card is done, which and who: "Dana marked it Done on Nov 5", "Archived by Dana on Nov 5", "Closed: the item went quiet on Nov 5". */
export function completeFinished(c: Pick<CardFacts, 'detail' | 'doneAt'>, n: Pick<CardNames, 'doneByName'>): string {
  const why = completeClosedOf(c.detail?.closed);
  const on = typeof c.detail?.closedOn === 'string' ? ` on ${formatShort(c.detail.closedOn)}` : doneOn(c.doneAt);
  return `${completeClosedWords(why, n.doneByName)}${why === 'gone' ? '' : on}`;
}

/**
 * WHY A CARD WHOSE WORK ENDED IS CLOSED, for every kind: "Closed: Organic India
 * was archived on Nov 5 by Dana", "Closed: Organic India's onboarding checklist
 * was deleted on Nov 5". The lifecycle's row names who; a brand archived with
 * no row names no one.
 */
export function endedFinished(why: EndedWhy, c: Pick<CardFacts, 'detail' | 'doneAt'>, n: Pick<CardNames, 'brandName' | 'doneByName'>): string {
  const on = typeof c.detail?.closedOn === 'string' ? ` on ${formatShort(c.detail.closedOn)}` : doneOn(c.doneAt);
  const by = n.doneByName ? ` by ${n.doneByName}` : '';
  const brand = n.brandName || 'The brand';
  switch (why) {
    case 'brand-archived': return `Closed: ${brand} was archived${on}${by}`;
    case 'checklist-archived': return `Closed: ${brand}'s onboarding checklist was archived${on}${by}`;
    case 'checklist-deleted': return `Closed: ${brand}'s onboarding checklist was deleted${on}${by}`;
  }
}

/** What an archived card was when it was archived, for the Archived tab: "3/7 · due Nov 3", "done · due Nov 2". */
function archivedWhat(c: CardFacts, counter: { done: number; target: number } | null): string {
  return [counter ? `${num(counter.done)}/${num(counter.target)}` : null, c.doneAt !== null ? 'done' : null, `due ${formatShort(c.dueOn)}`].filter(Boolean).join(' · ');
}

/** Which tab a card is in on `today`: the state for snoozed and archived, else by its due day. */
function tabOf(c: CardFacts, today: string): InboxTab {
  if (c.state === 'snoozed') return 'snoozed';
  if (c.state === 'archived') return 'archived';
  return c.dueOn > today ? 'upcoming' : 'today';
}

/**
 * ONE CARD AS THE PAGE DRAWS IT. `today` is the operations day.
 *
 * ⚠️ THE DONE COUNT IS CAPPED AT THE TARGET: past the freeze, decisions keep
 * counting (5 decided against a frozen 3), and "5/3" says nothing true. The day
 * row keeps the real number. "+N new" is the tick's `newSinceFreeze`, never a
 * subtraction here, so the page and the tick cannot disagree about it.
 *
 * ⚠️ A KIND THAT IS NEVER LATE IS NEVER LATE HERE EITHER: a daily counting card
 * still due yesterday in the minutes before the tick rolls it is today's work.
 */
export function inboxCard(c: CardFacts, n: CardNames, today: string): InboxCard {
  const def = kindDef(c.kind);
  const ws = isWorkstream(c.workstream) ? c.workstream : null;
  const done = c.state === 'done';
  const goesLate = def?.goesLate ?? true;
  const lateDays = goesLate ? Math.max(0, daysBetweenUtc(c.dueOn, today)) : 0;
  const counter = def?.counting && c.target !== null ? { done: Math.min(c.doneN ?? 0, c.target), target: c.target, newN: detailNum(c.detail, 'newSinceFreeze') } : null;
  const triage = c.kind === 'review-sample-requests' && c.detail !== null ? { yes: detailNum(c.detail, 'yes'), look: detailNum(c.detail, 'look'), no: detailNum(c.detail, 'no') } : null;

  let kindLabel = def?.label ?? c.kind;
  const monthly = c.kind === 'launch-campaign' && c.detail?.period === 'month';
  let line: string | null = null;
  if (c.kind === 'overdue-scripts') {
    // Which work is late names the card, as the prototype does; done, nothing is, and it keeps its plain name.
    const lateScripts = detailNum(c.detail, 'scriptsLate');
    const lateVideos = detailNum(c.detail, 'videosLate');
    const weeks = detailNum(c.detail, 'weeks');
    if (!done) kindLabel = overdueLabel(lateScripts, lateVideos);
    line = done ? null : lateWorkLine(lateScripts, lateVideos, weeks);
  }
  const scripts: ScriptPill[] | null = c.kind === 'review-scripts'
    ? listedScripts(c.detail).map((s) => ({ title: s.title.trim() || 'Untitled', grade: s.grade === null ? null : String(Math.round(s.grade)), tone: gradeTone(s.grade), done: s.done === 1 }))
    : null;
  const report = c.kind === 'weekly-report' ? reportStateOf(c.detail) : null;
  if (report !== null) {
    const week = detailText(c.detail, 'week');
    line = week ? `Week of ${formatShort(week)}` : null;
  }
  const section = c.kind === 'onboarding-section' ? { title: detailText(c.detail, 'title') ?? 'Onboarding section', dayLabel: detailText(c.detail, 'dayLabel') } : null;
  if (section !== null) line = section.dayLabel;
  if (c.kind === 'launch-campaign') {
    const week = typeof c.detail?.week === 'string' ? c.detail.week : null;
    const made = detailNum(c.detail, 'campaigns');
    // A month's card (the setting's cadence) names its month; a week's, its Sunday.
    const of = !week ? '' : monthly ? ` · ${MONTH.format(new Date(`${week}T00:00:00Z`))}` : ` · week of ${formatShort(week)}`;
    line = made > 0 ? `${plural(made, 'campaign')}${of}` : `No campaign yet${of}`;
  }

  const task = c.kind === 'ad-hoc' ? { title: (c.title ?? '').trim() || 'Untitled task', body: c.body?.trim() || null, addedBy: n.addedByName } : null;
  if (task !== null) line = task.body;
  if (c.kind === 'client-messages' && !done) {
    const waiting = detailNum(c.detail, 'owed');
    line = waiting > 0 ? `${plural(waiting, 'message')} waiting` : null;
  }
  // A review card counts down: what is left, and the day's confirmed beside it.
  const review = c.kind === 'review-action-items';
  const toReview = detailNum(c.detail, 'owed');
  if (review && !done) line = `${num(toReview)} to review${(c.doneN ?? 0) > 0 ? ` · ${num(c.doneN ?? 0)} confirmed today` : ''}`;
  const raw = c.kind === 'complete-action-item' ? cardItemOf(c.detail) : null;
  const item: ItemLine | null = raw === null
    ? null
    : {
        id: raw.id,
        title: raw.title.trim() || 'Untitled item',
        due: raw.due ? `due ${formatShort(raw.due)}` : 'no due date',
        assignee: raw.assignee === null ? null : (n.firstNameOf?.(raw.assignee) ?? raw.assignee),
        owes: raw.owesTag,
      };
  if (item !== null && !done) line = [item.owes, item.assignee ?? 'Unassigned', item.due].filter(Boolean).join(' · ');

  // A done card says why it is done (who or what did it explains, it doesn't credit). Work done in Reacher is
  // Reacher's, never a person's: a one-off card holds the time it was done there; a counting card holds only when a
  // pass noticed it, so it says no time. An overdue card says its own (`overdueFinished`).
  let finished: string | null = null;
  const ended = endedOf(c.detail?.ended);
  if (done) {
    if (ended !== null) finished = endedFinished(ended, c, n);
    else if (c.kind === 'overdue-scripts') finished = overdueFinished(c, n);
    else if (report !== null) finished = reportFinished(c, n);
    else if (section !== null) finished = sectionFinished(c, n);
    else if (task !== null) finished = `Done${n.doneByName ? ` by ${n.doneByName}` : ''}${c.doneAt === null ? '' : ` on ${formatShort(operationsDay(c.doneAt))}`}`;
    else if (c.kind === 'client-messages') finished = messagesFinished(c, n);
    else if (review) finished = reviewFinished(c, n);
    else if (c.kind === 'complete-action-item') finished = completeFinished(c, n);
    else if (c.doneVia === 'reacher') finished = def?.counting ? 'Done by Reacher' : c.doneAt !== null ? `Completed in Reacher at ${clock(c.doneAt)}` : 'Completed in Reacher';
    else if (n.doneByName) finished = `Done by ${n.doneByName}`;
    else finished = 'Done';
  }

  const tab = tabOf(c, today);
  const snoozed = c.state === 'snoozed' && c.snoozedUntil !== null;
  const due: DueTag = done
    ? { tone: 'green', text: finished ?? 'Done' }
    : snoozed
      ? { tone: 'plain', text: `Snoozed until ${formatWithWeekday(c.snoozedUntil ?? '')}` }
    : lateDays > 0
      ? { tone: 'red', text: `${plural(lateDays, 'day')} late · due ${formatShort(c.dueOn)}` }
      : c.dueOn <= today
        ? { tone: 'amber', text: 'Due today' }
        : { tone: 'plain', text: `Due ${formatWithWeekday(c.dueOn)}` };

  // A report's pill says its state, done or not: the list's own word, "Final v1".
  const miniN = counter
    ? `${num(counter.done)}/${num(counter.target)}`
    : report !== null
      ? reportState(report).label
      : c.doneVia === 'reacher' && done
      ? 'in Reacher'
      : done
        ? '✓'
        : review
          ? num(toReview)
        : c.kind === 'overdue-scripts' && lateDays > 0
          ? `${num(lateDays)}d late`
          : formatShort(c.dueOn);
  const name = task?.title ?? n.creatorName ?? section?.title ?? item?.title ?? n.brandName;
  const brandSub = n.creatorName !== null || section !== null || item !== null;
  const archived = c.state === 'archived';

  return {
    id: c.cardId,
    kind: c.kind,
    kindLabel,
    workstream: ws,
    cadence: monthly ? CADENCE_LABEL.monthly : def ? CADENCE_LABEL[def.cadence] : '',
    brand: c.brandSlug,
    brandName: n.brandName,
    name,
    sub: task !== null ? n.brandName || (task.addedBy ? `Added by ${task.addedBy}` : null) : brandSub ? n.brandName || null : n.podLabel,
    brandSub,
    pod: n.pod,
    podLabel: n.podLabel,
    dueOn: c.dueOn,
    lateDays,
    tab,
    done,
    counter,
    unit: c.kind === 'review-messages' ? 'replies owed' : c.kind === 'review-scripts' ? 'reviewed' : section !== null ? 'ticked' : c.kind === 'client-messages' ? 'answered today' : null,
    triage,
    scripts,
    report,
    item,
    toReview: review ? toReview : null,
    line,
    due,
    finished,
    seat: n.seat,
    noPod: n.noPod,
    away: n.away,
    from: n.from,
    active: n.active,
    href: n.href,
    slideOut: c.kind === 'overdue-scripts' && c.brandSlug !== null,
    task,
    snoozedUntil: snoozed ? c.snoozedUntil : null,
    archived: archived && c.archivedAt !== null ? { on: formatShort(operationsDay(c.archivedAt)), by: n.archivedByName, at: c.archivedAt, what: archivedWhat(c, counter) } : null,
    actions: {
      // The viewer's own work only: a card escalated away is its new seat's to put off.
      snooze: c.state === 'todo' && canSnooze(c.kind) && n.away === null,
      resnooze: c.state === 'snoozed' && canSnooze(c.kind) && n.away === null,
      unsnooze: c.state === 'snoozed',
      archive: !archived,
      unarchive: archived,
      done: task !== null && (c.state === 'todo' || c.state === 'snoozed'),
      reopen: task !== null && done,
      escalate: c.state === 'todo' && n.active,
    },
    // A creator writes for more than one brand, and every checklist has a Setup: their pills name the brand, or two read alike.
    mini: { label: (section !== null || item !== null) && n.brandName ? `${n.brandName} · ${name}` : n.creatorName !== null && n.brandName ? `${name} · ${n.brandName}` : name, n: miniN },
  };
}

// ─── The board's order ──────────────────────────────────────────────────────

const KIND_ORDER: readonly string[] = OPS_KINDS.map((k) => k.key);
const kindRank = (k: string): number => {
  const i = KIND_ORDER.indexOf(k);
  return i < 0 ? KIND_ORDER.length : i;
};

/** Owed by the viewer: not done, and not escalated away from them. What every count counts. */
export const owed = (c: InboxCard): boolean => !c.done && c.away === null;

/**
 * A group's order: the work owed first, oldest first; then the cards
 * escalated away, which the viewer may still finish; then the done ones,
 * struck through, at the bottom.
 */
export function sortCards(cards: readonly InboxCard[]): InboxCard[] {
  const rank = (c: InboxCard): number => (c.done ? 2 : c.away !== null ? 1 : 0);
  return [...cards].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (a.dueOn < b.dueOn ? -1 : a.dueOn > b.dueOn ? 1 : 0) ||
      kindRank(a.kind) - kindRank(b.kind) ||
      a.brandName.localeCompare(b.brandName) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}

export type StatusPick = 'all' | 'todo' | 'done';
export const STATUS_PICKS: readonly { key: StatusPick; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'todo', label: 'To do' },
  { key: 'done', label: 'Done' },
];

export function byStatus(cards: readonly InboxCard[], status: StatusPick): InboxCard[] {
  return cards.filter((c) => (status === 'todo' ? !c.done : status === 'done' ? c.done : true));
}

const wsLabel = (ws: Workstream | null): string => WORKSTREAMS.find((w) => w.key === ws)?.label ?? 'Ad hoc';

export interface AgeSection {
  key: string;
  label: string;
  cards: InboxCard[];
  /** What a folded section says it holds: the owed cards per workstream, "Affiliate Campaigns 3 · Client Comms 1". */
  folded: string;
  /** What its Archive all archives: the cards the viewer owes in it. None for Due today, which is today's work, not a backlog. */
  archivable: string[];
}

/** Today's cards in their age sections, oldest first; a section with nothing in it is not drawn. */
export function ageSections(cards: readonly InboxCard[]): AgeSection[] {
  return AGE_SECTIONS.map((s) => {
    const inIt = cards.filter((c) => ageSectionKey(c.lateDays) === s.key);
    const open = inIt.filter(owed);
    const folded = [...WORKSTREAMS.map((w) => w.key as Workstream | null), null]
      .map((w) => [wsLabel(w), open.filter((c) => c.workstream === w).length] as const)
      .filter(([, n]) => n > 0)
      .map(([l, n]) => `${l} ${num(n)}`)
      .join(' · ');
    return { key: s.key, label: s.label, cards: inIt, folded, archivable: s.key === '0' ? [] : open.filter((c) => c.actions.archive).map((c) => c.id) };
  }).filter((s) => s.cards.length > 0);
}

export interface KindRow {
  kind: string;
  label: string;
  cadence: string;
  /** "5 of 5 open". */
  open: string;
  cards: InboxCard[];
}

export interface CardGroup {
  key: string;
  title: string;
  /** "3 to do", or "all done". */
  todo: string;
  cards: InboxCard[];
  /** The same cards a row per task type, for the rows view. */
  rows: KindRow[];
  /** Grouped by task type: each row is the group, so it needs no title of its own. */
  byKind: boolean;
}

function kindRows(cards: readonly InboxCard[]): KindRow[] {
  const kinds = [...new Set(cards.map((c) => c.kind))].sort((a, b) => kindRank(a) - kindRank(b));
  return kinds.map((k) => {
    const of = cards.filter((c) => c.kind === k);
    return { kind: k, label: of[0].kindLabel, cadence: of[0].cadence, open: `${num(of.filter((c) => !c.done).length)} of ${num(of.length)} open`, cards: of };
  });
}

/**
 * THE GROUPS: by workstream, or by task type when a workstream is picked or
 * the viewer chose "Always by task type". By task type with no workstream
 * picked, a group's title names both ("Affiliate Campaigns · Review sample
 * requests"), so it still says where it belongs.
 */
export function cardGroups(cards: readonly InboxCard[], byKind: boolean, wsPicked: boolean): CardGroup[] {
  const make = (key: string, title: string, of: InboxCard[], kind: boolean): CardGroup => {
    const sorted = sortCards(of);
    const n = sorted.filter(owed).length;
    return { key, title, todo: n > 0 ? `${num(n)} to do` : 'all done', cards: sorted, rows: kindRows(sorted), byKind: kind };
  };
  if (byKind) {
    return [...new Set(cards.map((c) => c.kind))]
      .sort((a, b) => kindRank(a) - kindRank(b))
      .map((k) => {
        const of = cards.filter((c) => c.kind === k);
        const label = of[0].kindLabel;
        return make(k, wsPicked || of[0].workstream === null ? label : `${wsLabel(of[0].workstream)} · ${label}`, of, true);
      });
  }
  return [...WORKSTREAMS.map((w) => w.key as Workstream | null), null]
    .map((w) => make(w ?? AD_HOC, wsLabel(w), cards.filter((c) => c.workstream === w), false))
    .filter((g) => g.cards.length > 0);
}

export type Density = 'auto' | 'rows' | 'cards';
export const DENSITIES: readonly { key: Density; label: string; sub: string }[] = [
  { key: 'auto', label: 'Auto', sub: 'rows for all brands, cards for one' },
  { key: 'rows', label: 'Rows', sub: 'one row per task type' },
  { key: 'cards', label: 'Cards', sub: 'a card per brand' },
];

/** Rows (a line per task type, a pill per brand) or cards: rows across every brand, cards for one, unless the viewer chose. */
export const showRows = (density: Density, brandPicked: boolean): boolean => density === 'rows' || (density === 'auto' && !brandPicked);

// ─── Snooze and archive ─────────────────────────────────────────────────────

/** A snooze's quick picks: tomorrow, and the Monday after today ("Next Monday · Mon Nov 9"); on a Sunday, the one after tomorrow's. */
export function snoozePicks(today: string): { key: string; label: string; day: string; when: string }[] {
  const tomorrow = addDaysUtc(today, 1);
  const toMonday = (8 - dowOf(today)) % 7 || 7;
  const monday = addDaysUtc(today, toMonday === 1 ? 8 : toMonday);
  return [
    { key: 'tomorrow', label: 'Tomorrow', day: tomorrow, when: formatWithWeekday(tomorrow) },
    { key: 'monday', label: 'Next Monday', day: monday, when: formatWithWeekday(monday) },
  ];
}

/** The Archived tab's rows: newest archived first. */
export const archivedFirst = (cards: readonly InboxCard[]): InboxCard[] =>
  cards.filter((c) => c.archived !== null).sort((a, b) => (b.archived?.at ?? 0) - (a.archived?.at ?? 0) || a.id.localeCompare(b.id));

// ─── The task chip ──────────────────────────────────────────────────────────

/** The day as the chip and the summary count it: today's cards owed by the viewer, and how many of them are done. */
export interface DayTally {
  done: number;
  total: number;
}

/**
 * TODAY'S CARDS, DONE OF TOTAL: the summary line's "1 of 33 done today" and
 * the chip's "Inbox 1/33", one count. A card escalated away is in neither.
 */
export function dayTally(cards: readonly InboxCard[]): DayTally {
  const today = cards.filter((c) => c.tab === 'today' && c.away === null);
  return { done: today.filter((c) => c.done).length, total: today.length };
}

/**
 * THE NAV BADGE: today's cards still to do, of the same tally ("Inbox 6/30"
 * leaves 24), so the badge, the chip and the summary line never disagree.
 * Null at none: a badge at 0 is chrome.
 */
export function inboxBadgeOf(day: DayTally): { count: number; title: string } | null {
  const left = day.total - day.done;
  if (left <= 0) return null;
  return { count: left, title: `${num(left)} ${left === 1 ? 'task' : 'tasks'} left to do today in your Operations Inbox` };
}

/** The top bar's chip for an open card: "Review sample requests · Lumen Skincare", "3/37", "+5 new", ✓. */
export interface TaskChip {
  cardId: string;
  label: string;
  /** The counter, capped as the card draws it; a review card's items left ("57 to review"); null for a card with neither (launch a campaign). */
  n: string | null;
  /** "+5 new": arrivals past the frozen target. Not on a done card: they are the next day's. */
  newN: string | null;
  done: boolean;
  /** Who finished it, as the card says it. */
  finished: string | null;
  /** The path the card is carried under (`cardPlace`). */
  place: string | null;
}

export function taskChipOf(c: InboxCard): TaskChip {
  return {
    cardId: c.id,
    // A section is named by itself and its client ("Setup · Alpha", the prototype's): "Onboarding section" says nothing a section's own title doesn't.
    // An item to complete says so before its title: "Complete: Send the brief".
    label: c.item !== null ? `Complete: ${c.name}` : c.kind === 'onboarding-section' && c.brandName ? `${c.name} · ${c.brandName}` : c.name ? `${c.kindLabel} · ${c.name}` : c.kindLabel,
    // A review card counts down: how many items are left, which the chip watches go down.
    n: c.counter ? `${num(c.counter.done)}/${num(c.counter.target)}` : c.toReview !== null && !c.done ? `${num(c.toReview)} to review` : null,
    newN: c.counter && c.counter.newN > 0 && !c.done ? `+${num(c.counter.newN)} new` : null,
    done: c.done,
    finished: c.finished,
    place: cardPlace(c.kind, c.brand),
  };
}

// ─── The page ───────────────────────────────────────────────────────────────

export interface PickOption {
  value: string;
  label: string;
  count?: number;
  href: string;
  muted?: boolean;
}

export interface TabSegment {
  key: InboxTab;
  label: string;
  count: number;
  late: number;
  href: string;
}

export interface InboxPageModel {
  /** "1 of 33 done today", then " · 16 overdue · 2 escalated by you". */
  summary: { lead: string; rest: string };
  /** My / Active / All, for an admin only: everyone else always sees My. */
  scopes: { key: InboxScope; label: string; title: string; count: number; href: string }[] | null;
  scope: InboxScope;
  /** Null: the viewer sees one pod, so there is nothing to pick. */
  pods: PickOption[] | null;
  podPicked: string;
  brands: PickOption[];
  brandPicked: string;
  tabs: TabSegment[];
  workstreams: PickOption[];
  wsPicked: string;
  /** The cards on the board, every filter applied; the board orders them. */
  cards: InboxCard[];
  empty: string;
}

export interface InboxPageInput {
  /** Every card in the scope shown, before the pod, brand, tab and workstream filters. */
  cards: readonly InboxCard[];
  reading: InboxReading;
  /** The scope shown: the URL's, or the viewer's default. */
  scope: InboxScope;
  /** The scope the viewer's inbox opens on, which the URL leaves out. */
  defaultScope: InboxScope;
  isAdmin: boolean;
  /** For an admin: what each scope owes today, for the My / Active / All counts. */
  scopeCounts: Record<InboxScope, number> | null;
  /** A brand's or pod's name, for a pick that names one with no card in scope; null for one that names nothing. */
  brandName: (slug: string) => string | null;
  podName: (slug: string) => string | null;
  as: string | null;
}

/** The page from the cards in scope and the URL. */
export function inboxPage(i: InboxPageInput): InboxPageModel {
  const r = i.reading;
  const href = (patch: Partial<InboxReading>): string => inboxHref({ ...r, ...patch }, i.as);
  // The pod and brand tiers count the tab's own cards: under Archived, the archived ones; elsewhere, none of them.
  const live = i.cards.filter((c) => (c.tab === 'archived') === (r.tab === 'archived'));

  // A pick that names nothing is no pick; one that names a real pod or brand with nothing in scope stays lit, at 0.
  const pod = r.pod !== null && (r.pod === NO_POD_PICK || i.podName(r.pod) !== null) ? r.pod : null;
  const brand = r.brand !== null && i.brandName(r.brand) !== null ? r.brand : null;

  const inPod = (c: InboxCard): boolean => pod === null || c.pod === pod;
  const inBrand = (c: InboxCard): boolean => brand === null || c.brand === brand;
  const inTab = (c: InboxCard): boolean => c.tab === r.tab;
  const inWs = (c: InboxCard): boolean => r.ws === null || (r.ws === AD_HOC ? c.workstream === null : c.workstream === r.ws);
  const owedIn = (list: readonly InboxCard[]): number => list.filter((c) => owed(c) && inTab(c)).length;

  // The summary is the scope's day, whatever pod or brand is picked: what is owed today in it.
  const day = dayTally(i.cards);
  const overdue = i.cards.filter((c) => c.tab === 'today' && c.away === null && !c.done && c.lateDays > 0).length;
  const awayN = i.cards.filter((c) => c.tab === 'today' && c.away !== null && !c.done).length;
  const rest = [overdue > 0 ? `${num(overdue)} overdue` : '', awayN > 0 ? `${num(awayN)} escalated by you` : ''].filter(Boolean);
  const summary = day.total === 0
    ? { lead: 'Nothing due today', rest: rest.length ? ` · ${rest.join(' · ')}` : '' }
    : { lead: `${num(day.done)} of ${num(day.total)} done today`, rest: rest.length ? ` · ${rest.join(' · ')}` : '' };

  const scopes = i.isAdmin && i.scopeCounts
    ? SCOPES.map((s) => ({ ...s, count: i.scopeCounts![s.key], href: inboxHref({ ...r, scope: s.key === i.defaultScope ? null : s.key, pod: null, brand: null }, i.as) }))
    : null;

  // The pods this scope's cards sit in; the row only when there is more than one to pick between.
  const podKeys = [...new Set(live.map((c) => c.pod).filter((p): p is string => p !== null))];
  if (pod !== null && !podKeys.includes(pod)) podKeys.push(pod);
  const podLabel = (p: string): string => (p === NO_POD_PICK ? 'No pod' : (live.find((c) => c.pod === p)?.podLabel ?? i.podName(p) ?? p));
  const pods: PickOption[] | null = podKeys.length > 1
    ? [
        { value: '', label: 'All pods', count: owedIn(live), href: href({ pod: null, brand: null }) },
        ...podKeys
          .sort((a, b) => (a === NO_POD_PICK ? 1 : b === NO_POD_PICK ? -1 : podLabel(a).localeCompare(podLabel(b))))
          .map((p) => {
            const n = owedIn(live.filter((c) => c.pod === p));
            return { value: p, label: podLabel(p), count: n, href: href({ pod: p, brand: null }), muted: n === 0 };
          }),
      ]
    : null;

  const inThePod = live.filter(inPod);
  const brandKeys = [...new Set(inThePod.map((c) => c.brand).filter((b): b is string => b !== null))];
  if (brand !== null && !brandKeys.includes(brand)) brandKeys.push(brand);
  const brandLabel = (b: string): string => live.find((c) => c.brand === b)?.brandName ?? i.brandName(b) ?? b;
  const brands: PickOption[] = [
    { value: '', label: 'All brands', href: href({ brand: null }) },
    ...brandKeys
      .sort((a, b) => brandLabel(a).localeCompare(brandLabel(b)))
      .map((b) => {
        const n = owedIn(inThePod.filter((c) => c.brand === b));
        return { value: b, label: brandLabel(b), count: n, href: href({ brand: b }), muted: n === 0 };
      }),
  ];

  const scoped = i.cards.filter((c) => inPod(c) && inBrand(c));
  const tabs: TabSegment[] = TABS.map((t) => {
    const of = scoped.filter((c) => c.tab === t.key && owed(c));
    return { key: t.key, label: t.label, count: of.length, late: t.key === 'today' ? of.filter((c) => c.lateDays > 0).length : 0, href: href({ tab: t.key }) };
  });

  const view = scoped.filter(inTab);
  const wsN = (w: string | null): number => view.filter((c) => owed(c) && (w === AD_HOC ? c.workstream === null : c.workstream === w)).length;
  const workstreams: PickOption[] = [
    { value: '', label: 'All', count: view.filter(owed).length, href: href({ ws: null }) },
    ...[...WORKSTREAMS, { key: AD_HOC, label: 'Ad hoc' }].map((w) => {
      const n = wsN(w.key);
      return { value: w.key, label: w.label, count: n, href: href({ ws: w.key as WsPick }), muted: n === 0 };
    }),
  ];

  const cards = view.filter(inWs);
  const empty =
    r.tab === 'snoozed' ? 'Nothing snoozed.'
    : r.tab === 'upcoming' ? 'Nothing coming up.'
    : r.tab === 'archived' ? 'Nothing archived.'
    : i.scope === 'my' ? 'Nothing in your seats. Try Active or All.'
    : 'Nothing to do here.';

  return {
    summary,
    scopes,
    scope: i.scope,
    pods,
    podPicked: pod ?? '',
    brands,
    brandPicked: brand ?? '',
    tabs,
    workstreams,
    wsPicked: r.ws ?? '',
    cards,
    empty: i.scope === 'my' && !i.isAdmin && r.tab === 'today' ? 'Nothing in your seats today.' : empty,
  };
}

// ─── A person's moves, in words ─────────────────────────────────────────────

/** Why a new task wasn't added, in one sentence. */
export const TASK_REFUSAL: Record<string, string> = {
  no_title: 'Give the task a title.',
  too_long: 'That is too long: keep the title under 200 characters and the description under 4,000.',
  bad_day: 'Pick a due date.',
  bad_brand: "That brand isn't one you can add a task to.",
  bad_workstream: "That workstream isn't one the inbox knows.",
  no_seat: "Your account has no seat to put a task with no brand on yet. Pick a brand, or ask an admin.",
  off: 'Ad hoc tasks are switched off in the inbox settings.',
};

/** What a move did, for its toast: "Archived 12 tasks", "Snoozed until Fri Nov 6". */
export function movedToast(move: string, n: number, until: string | null): { title: string; sub: string | null } {
  const tasks = `${num(n)} task${n === 1 ? '' : 's'}`;
  switch (move) {
    case 'archive':
      return { title: n === 1 ? 'Archived' : `Archived ${tasks}`, sub: n === 1 ? 'Find it under Archived.' : 'Find them under Archived.' };
    case 'unarchive':
      return { title: n === 1 ? 'Back in the inbox' : `${tasks} back in the inbox`, sub: null };
    case 'snooze':
      return { title: 'Snoozed', sub: until ? `Back ${formatWithWeekday(until)}.` : null };
    case 'snooze-moved':
      return { title: 'Snooze moved', sub: until ? `Back ${formatWithWeekday(until)}.` : null };
    case 'unsnooze':
      return { title: 'Back in the inbox', sub: null };
    case 'done':
      return { title: 'Done', sub: null };
    case 'escalate':
      return { title: 'Escalated', sub: null };
    default:
      return { title: 'Back to do', sub: null };
  }
}
